import { QueryParams } from '../app/types/query-params';
import { getDateUpperBound } from './dateHelper';
import { getVersionFromString, transformResolutionAggregation } from './util';
import { DateTime } from 'luxon';

const replaceNullOptions = [
  'SKIP',
  'KEEP',
  'DROP',
  'PREV',
  'PREVIOUS',
  'PREVIOUS_INT',
  'PREVIOUS_DAY',
  'NEXT',
  'NEXT_INT',
  'NEXT_DAY',
];
/* global Intl, window */

export function generateUriParams(
  queryParams: QueryParams,
  queryStart?: DateTime,
  queryEnd?: DateTime,
  hpfc?: boolean,
  power_query: boolean = true
) {
  let url_params = '';
  if (queryParams.currency !== '') {
    url_params += '&currency=' + queryParams.currency;
  }
  if (queryParams.unit !== '') {
    url_params += '&unit=' + queryParams.unit;
  }
  if (queryStart) {
    url_params +=
      '&start_date=' + encodeURIComponent(queryStart.toFormat('yyyy-MM-dd'));
  }
  if (queryParams.auto_update) {
    if (queryStart !== null && !power_query) {
      if (!queryEnd) {
        let end_date = DateTime.now().plus({ days: 1 });
        url_params +=
          '&end_date=' + encodeURIComponent(end_date.toFormat('yyyy-MM-dd'));
      } else {
        let endDate = getDateUpperBound(queryEnd, queryParams.aggregation!);
        url_params +=
          '&end_date=' + encodeURIComponent(endDate.toFormat('yyyy-MM-dd'));
      }
    }
  } else {
    if (queryStart && queryEnd) {
      let endDate = getDateUpperBound(queryEnd, queryParams.aggregation!);
      url_params +=
        '&end_date=' + encodeURIComponent(endDate.toFormat('yyyy-MM-dd'));
    }
  }
  if (queryParams.resolution !== '' && !queryParams.latest_price) {
    url_params +=
      '&resolution=' +
      encodeURIComponent(
        transformResolutionAggregation(queryParams.resolution)!
      );
  }
  if (queryParams.aggregation !== '' && !queryParams.latest_price) {
    url_params +=
      '&aggregation=' +
      encodeURIComponent(
        transformResolutionAggregation(queryParams.aggregation)!
      );
  }
  hpfc = hpfc === undefined ? false : hpfc;
  if (queryParams.latest_price && !hpfc) {
    url_params += '&last_price=' + queryParams.latest_price;
  }
  if (
    queryParams.aggregation !== '' &&
    queryParams.aggregation_scope !== '' &&
    queryParams.aggregation_scope !== 'base'
  ) {
    url_params +=
      '&special_aggregation=' +
      encodeURIComponent(
        transformAggregationScope(queryParams.aggregation_scope!)
      );
  }
  if (
    queryParams.aggregation !== '' &&
    queryParams.aggregation_method !== '' &&
    !queryParams.latest_price
  ) {
    url_params += '&aggregation_method=' + queryParams.aggregation_method;
  }
  if (
    queryParams.timezone !== undefined &&
    queryParams.timezone !== null &&
    queryParams.timezone !== ''
  ) {
    url_params += '&time_zone=' + encodeURIComponent(queryParams.timezone);
  }
  if (
    queryParams.nv_default &&
    replaceNullOptions.includes(queryParams.nv_default.toUpperCase())
  ) {
    queryParams.nv_default =
      queryParams.nv_default.toUpperCase() === 'PREV'
        ? 'PREVIOUS'
        : queryParams.nv_default.toUpperCase();
    url_params +=
      '&replace_null=' +
      encodeURIComponent(queryParams.nv_default.toUpperCase());
  }
  if (queryParams.max_version !== '') {
    url_params +=
      '&max_version=' +
      encodeURIComponent(getVersionFromString(queryParams.max_version!)!);
  }
  if (
    queryParams.start_of_day !== undefined &&
    queryParams.start_of_day !== null
  ) {
    url_params +=
      '&start_of_day=' + encodeURIComponent(queryParams.start_of_day);
  }
  return url_params;
}

export function generateProductCodesUri(products: string[]) {
  let url_product_codes = '';
  for (let i = 0; i < products.length; i++) {
    if (i === 0) {
      url_product_codes += 'product_codes=' + encodeURIComponent(products[i]);
    } else {
      url_product_codes += '&product_codes=' + encodeURIComponent(products[i]);
    }
  }
  return url_product_codes;
}

/**
 * Combines generateProductCodesUri and generateUriParams for PowerQuery request
 */
export function generatePricesRequest(
  products: string[],
  queryParams: QueryParams,
  queryStart?: DateTime,
  queryEnd?: DateTime
) {
  let url_product_codes = generateProductCodesUri(products);
  let url_params = generateUriParams(
    queryParams,
    queryStart,
    queryEnd,
    false,
    true
  );
  return (
    window.api_url + '/prices/historic_data?' + url_product_codes + url_params
  );
}

export function generateForwardCurveRequest(
  products: string[],
  queryParams: QueryParams,
  queryStart: DateTime,
  queryEnd: DateTime
) {
  return generatePricesRequest(products, queryParams, queryStart, queryEnd);
}

export function generateFundamentalsRequest(
  fundamentals: string[],
  queryParams: QueryParams,
  queryStart?: DateTime,
  queryEnd?: DateTime
) {
  let url_product_codes = generateProductCodesUri(fundamentals);
  let url_params =
    generateFundamentalParams(queryParams, queryStart, queryEnd, true) +
    '&enforce_length=true';
  return window.api_url + '/time_series/data?' + url_product_codes + url_params;
}

export function generateFundamentalParams(
  queryParams: QueryParams,
  queryStart?: DateTime,
  queryEnd?: DateTime,
  power_query: boolean = true
) {
  let url_params = '';
  if (queryParams.currency !== '') {
    url_params += '&currency=' + queryParams.currency;
  }
  if (queryParams.unit !== '') {
    url_params += '&unit=' + queryParams.unit;
  }
  if (queryStart) {
    url_params +=
      '&start_date=' + encodeURIComponent(queryStart.toFormat('yyyy-MM-dd'));
  }
  if (queryParams.auto_update) {
    if (queryStart && !power_query) {
      if (!queryEnd) {
        let end_date = DateTime.now().plus({ days: 1 });
        url_params +=
          '&end_date=' + encodeURIComponent(end_date.toFormat('yyyy-MM-dd'));
      } else {
        let endDate = getDateUpperBound(queryEnd, queryParams.aggregation!);
        url_params +=
          '&end_date=' + encodeURIComponent(endDate.toFormat('yyyy-MM-dd'));
      }
    }
  } else {
    if (queryStart && queryEnd) {
      let endDate = getDateUpperBound(queryEnd, queryParams.aggregation!);
      url_params +=
        '&end_date=' + encodeURIComponent(endDate.toFormat('yyyy-MM-dd'));
    }
  }
  if (queryParams.resolution !== '' && !queryParams.latest_price) {
    url_params +=
      '&resolution=' +
      encodeURIComponent(
        transformResolutionAggregation(queryParams.resolution!)!
      );
  }
  if (queryParams.aggregation !== '' && !queryParams.latest_price) {
    url_params +=
      '&aggregation=' +
      encodeURIComponent(
        transformResolutionAggregation(queryParams.aggregation!)!
      );
  }
  if (queryParams.latest_price) {
    url_params += '&latest_value=' + queryParams.latest_price;
  }
  if (
    queryParams.aggregation !== '' &&
    queryParams.aggregation_scope !== '' &&
    queryParams.aggregation_scope !== 'base'
  ) {
    url_params +=
      '&special_aggregation=' +
      encodeURIComponent(
        transformAggregationScope(queryParams.aggregation_scope!)
      );
  }
  if (
    queryParams.aggregation !== '' &&
    queryParams.aggregation_method !== '' &&
    !queryParams.latest_price
  ) {
    url_params += '&aggregation_method=' + queryParams.aggregation_method;
  }
  if (
    queryParams.timezone !== undefined &&
    queryParams.timezone !== null &&
    queryParams.timezone !== ''
  ) {
    url_params += '&time_zone=' + queryParams.timezone;
  }
  if (
    queryParams.start_of_day !== undefined &&
    queryParams.start_of_day !== null
  ) {
    url_params +=
      '&start_of_day=' + encodeURIComponent(queryParams.start_of_day);
  }
  if (
    queryParams.nv_default &&
    replaceNullOptions.includes(queryParams.nv_default!.toUpperCase())
  ) {
    queryParams.nv_default =
      queryParams.nv_default.toUpperCase() === 'PREV'
        ? 'PREVIOUS'
        : queryParams.nv_default.toUpperCase();
    url_params += '&replace_null=' + encodeURIComponent(queryParams.nv_default);
  }
  return url_params;
}

function generateIdsUri(arr: string[]) {
  let url_ids = '';
  arr.forEach((item, idx) => {
    if (idx !== 0) {
      url_ids += '&ids=' + item;
    } else {
      url_ids += 'ids=' + item;
    }
  });
  return url_ids;
}

export function generateLivePricesRequest(
  ids: string[],
  unit: string,
  currency: string
) {
  let url_ids = generateIdsUri(ids);
  let url_params = '';
  if (unit !== undefined && unit !== null && unit !== '') {
    url_params += '&unit=' + encodeURIComponent(unit);
  }
  if (currency !== undefined && currency !== null && currency !== '') {
    url_params += '&currency=' + encodeURIComponent(currency);
  }
  return window.api_url + '/prices/live_data?' + url_ids + url_params;
}

function transformAggregationScope(aggregation_scope: string) {
  switch (aggregation_scope) {
    case 'base':
      return '';
    case 'peak':
      return 'Peak';
    case 'off_peak':
      return 'Off-Peak';
    default:
      return '';
  }
}
